<template>
  <div v-if="info && !headless" class="commodity-info">
    <h3 class="commodity-info__title">About this commodity</h3>
    <p class="commodity-info__unit">
      1 token = {{ info.token_is }} of {{ info.name }}
    </p>
    <p v-if="priceLine" class="commodity-info__price">{{ priceLine }}</p>
    <div v-if="movementRows.length" class="commodity-info__movement">
      <h4 class="commodity-info__subtitle">Price movement</h4>
      <div v-for="row in movementRows" :key="row.label" class="commodity-info__row">
        <span class="commodity-info__label">{{ row.label }}</span>
        <span class="commodity-info__value" :class="row.tone">{{ row.value }}</span>
      </div>
    </div>
    <!-- <p class="commodity-info__note">
      The metal is held by a custodian, and the token price follows the market
      price daily.
    </p>
    <p v-if="info.attribution" class="commodity-info__attribution">
      <a
        :href="info.attribution.url"
        target="_blank"
        rel="noopener noreferrer"
      >{{ info.attribution.text }}</a>
    </p> -->
  </div>
</template>

<script>
import api from "@/utils/api";
import { money as formatMoney, relativeTime } from "@/utils/exchange";
import { periodReturn, yearRange } from "@/utils/trade-view";

export default {
  name: "CommodityInfo",

  props: {
    tokenId: { type: String, default: "" },
    // The token's daily closes in the deal's currency, oldest first, and today's move: the
    // movement rows come from these so they are in the deal's currency, like everything else.
    closes: { type: Array, default: () => [] },
    currency: { type: String, default: "USD" },
    todayChange: { type: Number, default: null },
    // Load and emit the details without drawing this panel: the trade page shows them in its
    // cards, range bars and "What you own".
    headless: { type: Boolean, default: false },
  },
  // The page uses the same details for its price cards, so it gets them from here rather than
  // asking again.
  emits: ["loaded"],

  data() {
    return { info: null };
  },

  computed: {
    // Per token, in the deal's currency, from the token's own history. Each row is skipped when
    // there is not enough history for it.
    movementRows() {
      const money = (n) => formatMoney(n, this.currency, 2);
      const percent = (f) => {
        const n = Number(f);
        if (f === null || f === undefined || !Number.isFinite(n)) return null;
        return {
          value: `${n > 0 ? "+" : n < 0 ? "-" : ""}${Math.abs(n * 100).toFixed(2)}%`,
          tone: n > 0 ? "up" : n < 0 ? "down" : "",
        };
      };
      const rows = [];
      [
        ["Today", this.todayChange],
        ["Past month", periodReturn(this.closes, 30)],
        ["Past year", periodReturn(this.closes, 365)],
      ].forEach(([label, fraction]) => {
        const p = percent(fraction);
        if (p) rows.push({ label, ...p });
      });
      const range = yearRange(this.closes);
      if (range && range.high > range.low) {
        rows.push({ label: "52-week range", value: `${money(range.low)} – ${money(range.high)} per token`, tone: "" });
      }
      return rows;
    },

    // The spot converted at the rate the deal is priced at, so it reads in the deal's currency.
    priceLine() {
      const quote = this.info?.quote;
      const price = Number(quote?.price);
      if (!quote || !Number.isFinite(price) || price <= 0) return "";
      const unit = quote.quote_unit ? ` per ${quote.quote_unit}` : "";
      const when = relativeTime(quote.at);
      return `${this.info.name} ${this.info.price_basis === "front_month" ? "front-month future" : "spot"}: ${formatMoney(price, quote.currency || this.currency, 2)}${unit}${when ? `, updated ${when}` : ""}`;
    },
  },

  watch: {
    tokenId: {
      immediate: true,
      handler(id) {
        this.load(id);
      },
    },
  },

  methods: {
    // Any failure, including the 404 for a non-commodity deal, just hides the block.
    async load(id) {
      this.info = null;
      if (!id) return;
      try {
        const res = await api.getCommodityToken(id);
        const payload = res?.data?.data;
        if (this.tokenId === id && payload?.name && payload?.token_is) {
          this.info = payload;
          this.$emit("loaded", payload);
        }
      } catch (e) {
        this.info = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$text-primary: #0f172a;
$text-secondary: #475569;
$text-muted: #64748b;
$border-color: #e2e8f0;

.commodity-info {
  background: #ffffff;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 20px;

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $text-primary;
    margin: 0 0 8px;
  }

  &__unit {
    font-size: 15px;
    font-weight: 500;
    color: $text-primary;
    margin: 0 0 6px;
  }

  &__movement {
    border-top: 1px solid $border-color;
    margin: 12px 0;
    padding-top: 12px;
  }

  &__subtitle {
    font-size: 13px;
    font-weight: 600;
    color: $text-primary;
    margin: 0 0 6px;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    line-height: 1.8;
  }

  &__label {
    color: $text-secondary;
  }

  &__value {
    color: $text-primary;
    font-weight: 500;
    text-align: right;

    &.up {
      color: #4d84ff;
    }

    &.down {
      color: #e84343;
    }
  }

  &__price,
  &__note {
    font-size: 13px;
    line-height: 1.6;
    color: $text-secondary;
    margin: 0 0 6px;
  }

  &__attribution {
    font-size: 12px;
    margin: 8px 0 0;

    a {
      color: $text-muted;
      text-decoration: underline;
    }
  }
}
</style>
